/**
 * Display-only countdown to the lease deadline.
 *
 * Every tick recomputes from `Date.now()` rather than decrementing a counter,
 * so a throttled or suspended tab reports the correct remaining time on wake.
 *
 * `onReachZero` fires once per deadline; a renewed lease re-arms it. What that
 * means is the caller's call — today it ends the session outright rather than
 * asking for one more lease, which accepts that a locally-fast clock can close
 * a session a few seconds before the server would have.
 */
import { useEffect, useRef, useState } from 'react';

const TICK_MS = 1000;

const remainingSeconds = (expiresAtMs: number): number => Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));

interface UseSessionCountdownOptions {
    /** Absolute local-clock deadline; null when no lease is held. */
    expiresAtMs: number | null;
    onReachZero: () => void;
}

export function useSessionCountdown({ expiresAtMs, onReachZero }: UseSessionCountdownOptions): number | null {
    const [secondsLeft, setSecondsLeft] = useState<number | null>(() =>
        expiresAtMs === null ? null : remainingSeconds(expiresAtMs),
    );

    const onReachZeroRef = useRef(onReachZero);
    useEffect(() => {
        onReachZeroRef.current = onReachZero;
    }, [onReachZero]);

    useEffect(() => {
        if (expiresAtMs === null) {
            setSecondsLeft(null);
            return;
        }

        // A fresh deadline re-arms the trigger for the new lease.
        let hasFired = false;
        const tick = () => {
            const next = remainingSeconds(expiresAtMs);
            setSecondsLeft(next);
            if (next === 0 && !hasFired) {
                hasFired = true;
                onReachZeroRef.current();
            }
        };

        tick();
        const intervalId = window.setInterval(tick, TICK_MS);
        return () => window.clearInterval(intervalId);
    }, [expiresAtMs]);

    return secondsLeft;
}
